import { useState } from 'react';
import { useBulkAction, useFields } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { useSelection } from '@/stores/selection';
import { toast } from '@/stores/ui';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Input, Label, Select } from '@/components/Input';
import { isApiError } from '@/lib/api/errors';
import { fieldValueBody, type FieldValueDraft } from './fieldValue';

const EMPTY: FieldValueDraft = { text: '', number: '', date: '', options: [] };

/** Bulk "Set field…": pick one asset custom field and a value, apply to every selected asset in one bulk request. */
export function SetFieldDialog({ ids, onClose }: { ids: string[]; onClose: () => void }) {
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const fields = useFields(wsId);
  const bulk = useBulkAction(wsId);
  const list = (fields.data ?? []).filter((f) => f.applies_to === 'asset');
  const [fieldId, setFieldId] = useState('');
  const [draft, setDraft] = useState<FieldValueDraft>(EMPTY);
  const field = list.find((f) => f.id === fieldId) ?? null;
  const body = field ? fieldValueBody(field, draft) : null;
  const apply = () => {
    if (!field || !body) return;
    bulk.mutate(
      { asset_ids: ids, action: 'set_custom_field', params: { field_id: field.id, value: body } },
      {
        onSuccess: (r) => {
          const res = r as { succeeded?: string[]; failed?: { asset_id: string; message?: string }[] };
          const ok = res.succeeded?.length ?? ids.length;
          const failed = res.failed?.length ?? 0;
          toast(failed ? { kind: 'error', title: `${field.name} set on ${ok} of ${ids.length}`, body: res.failed?.[0]?.message } : { kind: 'success', title: `${field.name} set on ${ok} ${ok === 1 ? 'asset' : 'assets'}` });
          useSelection.getState().clear();
        },
        onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not set field' }),
      },
    );
    onClose();
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Set field on ${ids.length} ${ids.length === 1 ? 'asset' : 'assets'}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!body || bulk.isPending} onClick={apply}>
            Apply
          </Button>
        </>
      }
    >
      {list.length === 0 ? (
        <p className="text-sm text-fg-muted">{fields.isPending ? 'Loading fields…' : 'No asset fields yet. Create one under Settings › Fields & tags.'}</p>
      ) : (
        <div className="space-y-3" data-set-field-dialog>
          <div>
            <Label htmlFor="sf-field">Field</Label>
            <Select
              id="sf-field"
              value={fieldId}
              onChange={(e) => {
                setFieldId(e.target.value);
                setDraft(EMPTY);
              }}
            >
              <option value="">Choose a field…</option>
              {list.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </Select>
          </div>
          {field && (
            <div>
              <Label htmlFor="sf-value">Value</Label>
              {field.type === 'text' && <Input id="sf-value" autoFocus value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} />}
              {field.type === 'number' && <Input id="sf-value" type="number" autoFocus value={draft.number} onChange={(e) => setDraft({ ...draft, number: e.target.value })} />}
              {field.type === 'date' && <Input id="sf-value" type="date" autoFocus value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />}
              {field.type === 'select' && (
                <Select id="sf-value" value={draft.options[0] ?? ''} onChange={(e) => setDraft({ ...draft, options: e.target.value ? [e.target.value] : [] })}>
                  <option value="">—</option>
                  {field.options?.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              )}
              {field.type === 'multi_select' && (
                <div className="flex flex-wrap gap-1" id="sf-value" role="group" aria-label="Options">
                  {field.options?.map((o) => {
                    const on = draft.options.includes(o.id);
                    return (
                      <button
                        key={o.id}
                        type="button"
                        aria-pressed={on}
                        className={`rounded-full border px-2 py-0.5 text-xs ${on ? 'border-accent bg-accent/10 text-accent' : 'border-line text-fg-muted'}`}
                        onClick={() => setDraft({ ...draft, options: on ? draft.options.filter((x) => x !== o.id) : [...draft.options, o.id] })}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
